/**
 * 「このプロジェクトを人に渡す」の画面（/p/:projectId/:env/transfer・2026-10-21 core/handoff-1a・第 1a 段 = 持ち主を移すだけ）。
 * 判断（メールの入力・一覧のキー → 文）は projectTransfer.ts（テストあり）。API の正は apps/server/src/modules/platform/projectTransferRoutes.ts。
 *
 * 1 画面に置く物（指示書 4「利用者に何が移るか / 残るかを 1 画面で」）: 何が移る / あなたに残る / 外れる の 3 つの一覧・入れ直しが要る物の名前・
 *   開いている招き（取り消し）か招きのフォーム。WHY 招きのフォームより先に一覧を置くか: 渡すと開けなくなる（「残る」を選べば共同作業者 = 1b）・お金の境目がある、を
 *   読んでから押してほしい。
 * 招くときの本人の確認: 共通の再認証（components/Reauth.tsx の run）。断られたら（400 reauthentication_required）ダイアログ → 送り直し。
 *   「Google で確認」で画面を離れて戻ったときは、入力をもう一度してもらう（取り返しのつかない操作ではないので自動では送らない = Reauth.tsx の WHY）。
 * 72 時間の止め: サーバーの securityHoldUntil があればフォームを出さずに日付だけ出す（押してから断られる、を避ける）。
 */
import { useState } from 'react';
import { api, type ApiError } from '../api';
import { EmailUnverifiedNotice } from '../components/EmailUnverified';
import { useReauth } from '../components/Reauth';
import { ConfirmDialog, ErrorState, PageHeader, Spinner } from '../components/ui';
import { isEmailUnverified } from '../emailVerify';
import { useResource } from '../hooks';
import { errorText, formatDate, useT } from '../i18n';
import { projectEyebrow } from '../projectNav';
import { inviteEmailState, outgoingPath, type OutgoingView, type TransferView } from '../projectTransfer';
import { useSession } from '../session';
import { HandoffLists } from '../components/HandoffLists';
import { useShell } from './ProjectShell';

const INVITE_ACTION = 'transfer:invite';
/** サーバーのエラーコード → 画面の文（errorText の overrides） */
const ERRORS = { transfer_pending: 'transfer.error.pending', transfer_to_self: 'transfer.invite.self', transfer_security_hold: 'transfer.error.hold' } as const;

export function TransferPage() {
  const { project } = useShell();
  const { developer } = useSession();
  const { t } = useT();
  const reauth = useReauth();
  const view = useResource<OutgoingView>(outgoingPath(project.id));
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  // 渡した後も共同作業者として手伝う（2026-10-22 core/handoff-1b・サーバーの stayAsMember）。既定は残らない = 1a と同じ
  const [stay, setStay] = useState(false);

  const state = inviteEmailState(email, developer?.email);
  const send = async () => {
    if (state !== 'ok' || busy) return;
    setBusy(true);
    setError(null);
    setUnverified(false);
    setDone(null);
    try {
      const r = await reauth.run((proof) => api<{ transfer: TransferView }>(outgoingPath(project.id), { method: 'POST', body: { email: email.trim(), stayAsMember: stay, ...proof } }), { action: INVITE_ACTION });
      if (r === null) return; // 確認を取り消した = 何も送っていない
      setDone(t('transfer.invite.sent', { email: r.transfer.toEmail }));
      setEmail('');
      view.reload();
    } catch (e) {
      if (isEmailUnverified(e as ApiError)) setUnverified(true);
      else setError(errorText(e as ApiError, ERRORS));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(outgoingPath(project.id), { method: 'DELETE' });
      setConfirmCancel(false);
      setDone(t('transfer.cancel.done'));
      view.reload();
    } catch (e) {
      setError(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  const v = view.data;
  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('transfer.title')} lead={t('transfer.lead')} />
      {reauth.dialog}
      {view.loading && !v && <Spinner />}
      {view.error && <ErrorState error={view.error} onRetry={view.reload} />}
      {done && (
        <p className="alert alert-ok" role="status">
          {done}
        </p>
      )}
      {error && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}
      {v && (
        <>
          <HandoffLists handoff={v.handoff} details={v.details} side="owner" />
          <p className="alert alert-warn">{t((v.transfer ? v.transfer.stayAsMember : stay) ? 'transfer.noAccessStay' : 'transfer.noAccess')}</p>

          {v.transfer ? (
            <section className="card">
              <h2 className="card-title">{t('transfer.pending.title', { email: v.transfer.toEmail })}</h2>
              <p>{t('transfer.pending.body', { date: formatDate(v.transfer.expiresAt) })}</p>
              <button type="button" className="btn btn-ghost" onClick={() => setConfirmCancel(true)} disabled={busy}>
                {t('transfer.pending.cancel')}
              </button>
            </section>
          ) : v.securityHoldUntil ? (
            <p className="alert alert-warn" role="status">
              {t('transfer.hold', { date: formatDate(v.securityHoldUntil) })}
            </p>
          ) : (
            <section className="card">
              <h2 className="card-title">{t('transfer.invite.title')}</h2>
              <form
                className="form-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  void send();
                }}
              >
                <label className="field">
                  <span>{t('transfer.invite.label')}</span>
                  <input type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} required />
                </label>
                <p className="small muted">{t('transfer.invite.help', { days: v.expiresInDays })}</p>
                <label className="check">
                  <input type="checkbox" checked={stay} onChange={(e) => setStay(e.target.checked)} disabled={busy} />
                  <span>{t('transfer.stay.label')}</span>
                </label>
                <p className="small muted">{t('transfer.stay.help')}</p>
                {email.trim() && state === 'invalid' && <p className="small muted">{t('transfer.invite.invalid')}</p>}
                {state === 'self' && <p className="field-error">{t('transfer.invite.self')}</p>}
                {unverified && <EmailUnverifiedNotice />}
                <button type="submit" className="btn btn-primary" disabled={busy || state !== 'ok'}>
                  {busy ? t('transfer.invite.sending') : t('transfer.invite.send')}
                </button>
              </form>
            </section>
          )}
        </>
      )}
      <ConfirmDialog
        open={confirmCancel}
        title={t('transfer.cancel.confirmTitle')}
        confirmLabel={t('transfer.pending.cancel')}
        tone="danger"
        busy={busy}
        onConfirm={() => void cancel()}
        onClose={() => setConfirmCancel(false)}
      >
        <p>{t('transfer.cancel.confirmBody')}</p>
      </ConfirmDialog>
    </>
  );
}
